import { Loader2, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CursorPager } from '@/components/ui/CursorPager'
import { envelopeActionLabel, type Envelope } from '@/lib/docusign-data'
import type { Freshness } from '@/lib/system-intelligence'
import { FreshnessNote, PanelEmpty, PanelError, PanelLoading } from './PanelState'

const columns = ['Recipient', 'Envelope ID', 'Sent Date', 'Status', 'Action']

const statusStyle: Record<Envelope['tone'], { cls: string; dot: string }> = {
  waiting: { cls: 'border-degraded/40 bg-degraded/10 text-degraded', dot: 'bg-degraded' },
  auth_failed: {
    cls: 'border-critical/40 bg-critical/10 text-critical-bright',
    dot: 'bg-critical',
  },
  delivered: { cls: 'border-healthy/30 bg-healthy/10 text-healthy', dot: 'bg-healthy' },
  declined: { cls: 'border-line-bright bg-surface-3 text-fg-muted', dot: 'bg-fg-subtle' },
  neutral: { cls: 'border-line-bright bg-surface-3 text-fg-muted', dot: 'bg-fg-subtle' },
}

interface EnvelopeQueueTableProps {
  envelopes: Envelope[]
  /** The search box's text, held by the page so it can reach the API. */
  query: string
  onQueryChange: (next: string) => void
  /** Chase an envelope — reminder or fresh link, depending on its status. */
  onAction: (envelope: Envelope) => void
  /** Rows currently mid-request, by envelope row id. */
  pendingId?: string | null
  /** Rows whose action is on cooldown, mapped to when it lifts. */
  cooldowns?: Record<string, Date>
  freshness?: Freshness
  isLoading?: boolean
  isFetching?: boolean
  error?: unknown
  onRetry?: () => void
  /**
   * Page controls. The state lives on the page rather than here, because the
   * cursor is part of the request — unlike the incident log, which pages rows it
   * already holds.
   */
  pager?: {
    page: number
    pageSize: number
    hasNext: boolean
    onPrev: () => void
    onNext: () => void
    onPageSizeChange: (size: number) => void
  }
}

/** Rows per page. Capped at 100 — the endpoint clamps any larger `limit` to it. */
export const ENVELOPE_PAGE_SIZES = [10, 25, 50, 100] as const

/**
 * Envelopes still in flight, with the one action each one needs.
 *
 * Search is sent to the API rather than applied to the loaded rows: the endpoint
 * is cursor-paged, so filtering locally would search one page and quietly report
 * "no matches" for an envelope that exists two pages down — the exact case
 * someone arriving from a support ticket with an ID is in.
 *
 * The action a row offers is the server's `suggested_action`, not something
 * derived from the status here. It is the side that knows whether a reminder was
 * already sent, and it is the side that will reject the call with a 409 if one
 * was; asking it is cheaper than getting the rule wrong in two places.
 */
export function EnvelopeQueueTable({
  envelopes,
  query,
  onQueryChange,
  onAction,
  pendingId,
  cooldowns,
  freshness,
  isLoading,
  isFetching,
  error,
  onRetry,
  pager,
}: EnvelopeQueueTableProps) {
  const now = Date.now()

  return (
    <section className="border-line bg-surface rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <h2 className="text-fg-muted flex items-center gap-2 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          Active Envelopes Queue
          {isFetching && !isLoading && <Loader2 aria-hidden className="size-3.5 animate-spin" />}
        </h2>
        <div className="relative">
          <Search className="text-fg-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            aria-label="Search envelopes by ID or recipient"
            placeholder="Search ID…"
            className="border-line bg-input text-fg placeholder:text-fg-subtle focus:border-primary focus:ring-primary/20 h-10 w-full rounded-lg border pr-4 pl-9 text-sm transition-colors outline-none focus:ring-2 sm:w-64"
          />
        </div>
      </div>

      {isLoading ? (
        <PanelLoading />
      ) : error ? (
        <PanelError error={error} onRetry={onRetry} />
      ) : envelopes.length === 0 ? (
        <>
          <PanelEmpty
            label={query ? `No envelopes match “${query}”` : 'No envelopes are in flight.'}
          />
          {/* An empty page past the first still needs a way back — otherwise the
              only exit from it is a reload. */}
          {pager && pager.page > 1 && (
            <CursorPager
              {...pager}
              pageSizes={ENVELOPE_PAGE_SIZES}
              rowCount={0}
              isFetching={isFetching}
              className="border-line border-t px-5 py-3"
            />
          )}
        </>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse text-sm">
              <thead>
                <tr className="border-line bg-surface-2 border-y">
                  {columns.map((col) => (
                    <th
                      key={col}
                      className={cn(
                        'text-fg-muted px-5 py-3 font-mono text-[13px] font-normal',
                        col === 'Action' ? 'text-right' : 'text-left',
                      )}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {envelopes.map((envelope) => {
                  const status = statusStyle[envelope.tone]
                  const label = envelopeActionLabel[envelope.action]
                  const cooldown = cooldowns?.[envelope.id]
                  const onCooldown = Boolean(cooldown && cooldown.getTime() > now)
                  const busy = pendingId === envelope.id

                  return (
                    <tr
                      key={envelope.id}
                      className="border-line hover:bg-surface-2 border-b transition-colors"
                    >
                      <td className="text-fg px-5 py-4">
                        {envelope.recipient}
                        {envelope.office && (
                          <span className="text-fg-subtle mt-0.5 block font-mono text-[11px]">
                            {envelope.office}
                            {envelope.financePartner ? ` · ${envelope.financePartner}` : ''}
                          </span>
                        )}
                      </td>
                      <td className="text-fg-muted px-5 py-4 font-mono text-[13px] whitespace-nowrap">
                        {envelope.envelopeId}
                      </td>
                      <td className="text-fg-muted px-5 py-4 font-mono text-[13px] whitespace-nowrap">
                        {envelope.sentDate}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={cn(
                            'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[13px] font-semibold whitespace-nowrap',
                            status.cls,
                          )}
                        >
                          <span className={cn('size-1.5 rounded-full', status.dot)} />
                          {envelope.statusLabel}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {label ? (
                          <button
                            type="button"
                            onClick={() => onAction(envelope)}
                            disabled={busy || onCooldown}
                            title={
                              onCooldown
                                ? `Already actioned — available again at ${cooldown!.toLocaleTimeString()}`
                                : undefined
                            }
                            className={cn(
                              // nowrap: "Send Reminder" and "Resend Link" are two
                              // words in a right-aligned column with no minimum
                              // width, so without it the label breaks across two
                              // lines and the button grows taller than its row.
                              'border-line-bright text-fg focus-visible:ring-primary/30 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap transition-colors outline-none focus-visible:ring-2',
                              busy || onCooldown
                                ? 'cursor-not-allowed opacity-50'
                                : 'hover:bg-surface-3',
                            )}
                          >
                            {busy && <Loader2 aria-hidden className="size-3.5 animate-spin" />}
                            {onCooldown ? 'On cooldown' : label}
                          </button>
                        ) : (
                          <span className="text-fg-subtle text-[13px] whitespace-nowrap">
                            No action needed
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {pager && (
            <CursorPager
              {...pager}
              pageSizes={ENVELOPE_PAGE_SIZES}
              rowCount={envelopes.length}
              isFetching={isFetching}
              className="border-line border-t px-5 py-3"
            />
          )}

          <FreshnessNote freshness={freshness} className="px-5 pb-4" />
        </>
      )}
    </section>
  )
}
